/* eslint-disable react/button-has-type */
import { memo, useEffect, useState } from 'react';

import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FirstPageRoundedIcon from '@mui/icons-material/FirstPageRounded';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LastPageRoundedIcon from '@mui/icons-material/LastPageRounded';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import QueueSharpIcon from '@mui/icons-material/QueueSharp';
import SplitscreenOutlinedIcon from '@mui/icons-material/SplitscreenOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import SwitchLeftOutlinedIcon from '@mui/icons-material/SwitchLeftOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import { Box, IconButton, Stack, styled, Tooltip } from '@mui/material';
import Button from '@mui/material/Button/Button';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { ReactSVG } from 'react-svg';

import alignCenter from 'assets/icons/24px/editor/text-align-center.svg';
import alignJustify from 'assets/icons/24px/editor/text-align-justified.svg';
import alignLeft from 'assets/icons/24px/editor/text-align-left.svg';
import alignRight from 'assets/icons/24px/editor/text-align-right.svg';

import { ColorPicker } from '../../InteractiveMapControl/MapConfigCreator/controls/ColorPicker/index';
import { TableEditorDialog } from '../TableEditorDialog';

const tableHTML = `
                <table>
                    <tbody>
                        <tr>
                            <th>Заголовок 1</th>
                            <th>Заголовок 2</th>
                            <th>Заголовок 3</th>
                            <th>Заголовок 4</th>
                        </tr>
                        <tr>
                            <td>Значение</td>
                            <td>Значение</td>
                            <td>Значение</td>
                            <td>Значение</td>
                        </tr>
                    </tbody>
                </table>
`;

const ToolBarButton = styled(IconButton)`
    border-radius: 0;

    &.is-active {
        color: ${({ theme }) => theme.palette.primary.main};
        background-color: ${({ theme }) => theme.palette.grey[200]};
    }

    &.local-svg {
        svg {
            path {
                stroke: rgba(0, 0, 0, 0.54);
            }
        }
    }
`;

ToolBarButton.defaultProps = {
    size: 'small',
};

const MenuBar = ({ editor }: { editor: Editor | null }): JSX.Element | null => {
    if (!editor) {
        return null;
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            sx={{
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '8px',
                overflowY: 'visible',
                display: 'flex',
                gap: '20px',
            }}
        >
            <ToolBarButton
                onClick={() =>
                    editor
                        .chain()
                        .focus()
                        .insertContent(tableHTML, {
                            parseOptions: {
                                preserveWhitespace: false,
                            },
                        })
                        .run()
                }
            >
                <Tooltip title="Добавить таблицу" placement="top-end" arrow>
                    <QueueSharpIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                disabled={!editor.can().addColumnBefore()}
            >
                <Tooltip
                    title="Добавить колонку перед"
                    placement="top-end"
                    arrow
                >
                    <FirstPageRoundedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>

            <ToolBarButton
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                disabled={!editor.can().addColumnAfter()}
            >
                <Tooltip
                    title="Добавить колонку после"
                    placement="top-end"
                    arrow
                >
                    <LastPageRoundedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().deleteColumn().run()}
                disabled={!editor.can().deleteColumn()}
            >
                <Tooltip title="Удалить колонку" placement="top-end" arrow>
                    <BackspaceOutlinedIcon
                        color="inherit"
                        sx={{
                            transform: 'rotate(-90deg)',
                        }}
                    />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().deleteRow().run()}
                disabled={!editor.can().deleteRow()}
            >
                <Tooltip title="Удалить строку" placement="top-end" arrow>
                    <BackspaceOutlinedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().addRowBefore().run()}
                disabled={!editor.can().addRowBefore()}
            >
                <Tooltip
                    title="Добавить строку перед"
                    placement="top-end"
                    arrow
                >
                    <FirstPageRoundedIcon
                        color="inherit"
                        sx={{
                            transform: 'rotate(90deg)',
                        }}
                    />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().addRowAfter().run()}
                disabled={!editor.can().addRowAfter()}
            >
                <Tooltip
                    title="Добавить строку после"
                    placement="top-end"
                    arrow
                >
                    <FirstPageRoundedIcon
                        color="inherit"
                        sx={{
                            transform: 'rotate(-90deg)',
                        }}
                    />
                </Tooltip>
            </ToolBarButton>

            <ToolBarButton
                onClick={() => editor.chain().focus().mergeCells().run()}
                disabled={!editor.can().mergeCells()}
            >
                <Tooltip title="Объединить ячейки" placement="top-end" arrow>
                    <ViewStreamOutlinedIcon
                        color="inherit"
                        sx={{
                            transform: 'rotate(-90deg)',
                        }}
                    />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().splitCell().run()}
                disabled={!editor.can().splitCell()}
            >
                <Tooltip title="Разбить ячейки" placement="top-end" arrow>
                    <ViewAgendaOutlinedIcon
                        color="inherit"
                        sx={{
                            transform: 'rotate(-90deg)',
                        }}
                    />
                </Tooltip>
            </ToolBarButton>

            <ToolBarButton
                onClick={() =>
                    editor.chain().focus().toggleHeaderColumn().run()
                }
                disabled={!editor.can().toggleHeaderColumn()}
            >
                <Tooltip
                    title="Сделать заголовком колонки"
                    placement="top-end"
                    arrow
                >
                    <SwitchLeftOutlinedIcon
                        color="inherit"
                        sx={{
                            transform: 'rotate(-90deg)',
                        }}
                    />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                disabled={!editor.can().toggleHeaderRow()}
            >
                <Tooltip
                    title="Сделать заголовком строки"
                    placement="top-end"
                    arrow
                >
                    <SwitchLeftOutlinedIcon
                        color="inherit"
                        sx={{
                            transform: 'rotate(-180deg)',
                        }}
                    />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().toggleHeaderCell().run()}
                disabled={!editor.can().toggleHeaderCell()}
            >
                <Tooltip
                    title="Сделать ячейку заголовком"
                    placement="top-end"
                    arrow
                >
                    <StarBorderOutlinedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>
            <ToolBarButton
                onClick={() => editor.chain().focus().mergeOrSplit().run()}
                disabled={!editor.can().mergeOrSplit()}
            >
                <Tooltip
                    title="Объединить / Разделить"
                    placement="top-end"
                    arrow
                >
                    <SplitscreenOutlinedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>

            <ToolBarButton
                type="button"
                onClick={() => editor.chain().focus().fixTables().run()}
                disabled={!editor.can().fixTables()}
            >
                <Tooltip
                    title="Проверяет все таблицы в документе и при необходимости исправляет их"
                    placement="top-end"
                    arrow
                >
                    <AutoFixHighOutlinedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>

            <ToolBarButton
                type="button"
                onClick={() => editor.chain().focus().goToPreviousCell().run()}
                disabled={!editor.can().goToPreviousCell()}
            >
                <Tooltip
                    title="Перейти к предыдущей ячейке"
                    placement="top-end"
                    arrow
                >
                    <NavigateNextOutlinedIcon
                        sx={{
                            transform: 'rotate(180deg)',
                        }}
                        color="inherit"
                    />
                </Tooltip>
            </ToolBarButton>

            <ToolBarButton
                type="button"
                onClick={() => editor.chain().focus().goToNextCell().run()}
                disabled={!editor.can().goToNextCell()}
            >
                <Tooltip
                    title="Перейти к следующей ячейке"
                    placement="top-end"
                    arrow
                >
                    <NavigateNextOutlinedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>

            <ToolBarButton
                onClick={() => editor.chain().focus().deleteTable().run()}
                disabled={!editor.can().deleteTable()}
            >
                <Tooltip
                    title="Удалить выбранную таблицу"
                    placement="top-end"
                    arrow
                >
                    <DeleteOutlinedIcon color="inherit" />
                </Tooltip>
            </ToolBarButton>

            <ColorPicker
                onColorChange={(color) =>
                    editor
                        .chain()
                        .focus()
                        .setCellAttribute('backgroundColor', color)
                        .run()
                }
                color="red"
                label="Заливка ячейки"
            />

            <Box sx={{ flex: 1 }} />

            <Box sx={{ marginTop: '-20px', display: 'flex' }}>
                <ToolBarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    disabled={!editor.can().chain().focus().toggleBold().run()}
                    className={editor.isActive('bold') ? 'is-active' : ''}
                >
                    <FormatBoldIcon color="inherit" />
                </ToolBarButton>
                <ToolBarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    disabled={
                        !editor.can().chain().focus().toggleItalic().run()
                    }
                    className={editor.isActive('italic') ? 'is-active' : ''}
                >
                    <FormatItalicIcon color="inherit" />
                </ToolBarButton>
                <ToolBarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    disabled={
                        !editor.can().chain().focus().toggleStrike().run()
                    }
                    className={editor.isActive('strike') ? 'is-active' : ''}
                >
                    <FormatStrikethroughIcon color="inherit" />
                </ToolBarButton>
                <ToolBarButton
                    onClick={() =>
                        editor.chain().focus().toggleUnderline().run()
                    }
                    disabled={
                        !editor.can().chain().focus().toggleUnderline().run()
                    }
                    className={editor.isActive('underline') ? 'is-active' : ''}
                >
                    <FormatUnderlinedIcon color="inherit" />
                </ToolBarButton>
                <ToolBarButton
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .toggleSuperscript()
                            .unsetSubscript()
                            .run()
                    }
                    disabled={
                        !editor
                            .can()
                            .chain()
                            .focus()
                            .toggleSuperscript()
                            .unsetSubscript()
                            .run()
                    }
                    className={
                        editor.isActive('superscript') ? 'is-active' : ''
                    }
                >
                    <SuperscriptIcon color="inherit" />
                </ToolBarButton>
                <ToolBarButton
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .toggleSubscript()
                            .unsetSuperscript()
                            .run()
                    }
                    disabled={
                        !editor
                            .can()
                            .chain()
                            .focus()
                            .toggleSubscript()
                            .unsetSuperscript()
                            .run()
                    }
                    className={editor.isActive('subscript') ? 'is-active' : ''}
                >
                    <SubscriptIcon color="inherit" />
                </ToolBarButton>
                <ToolBarButton
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .setCellAttribute('align', 'justify')
                            .run()
                    }
                    className="local-svg"
                >
                    <ReactSVG src={alignJustify} />
                </ToolBarButton>

                <ToolBarButton
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .setCellAttribute('align', 'left')
                            .run()
                    }
                    className="local-svg"
                >
                    <ReactSVG src={alignLeft} />
                </ToolBarButton>

                <ToolBarButton
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .setCellAttribute('align', 'center')
                            .run()
                    }
                    className="local-svg"
                >
                    <ReactSVG src={alignCenter} />
                </ToolBarButton>

                <ToolBarButton
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .setCellAttribute('align', 'right')
                            .run()
                    }
                    className="local-svg"
                >
                    <ReactSVG src={alignRight} />
                </ToolBarButton>
                <ColorPicker
                    onColorChange={(color) =>
                        editor
                            .chain()
                            .focus()
                            .setCellAttribute('fontColor', color)
                            .run()
                    }
                    color="red"
                    label="Цвет шрифта"
                />
            </Box>
        </Stack>
    );
};

type RichTextEditorProps = {
    value?: string;
    onChange?: (value: string) => void;
};

const EditorMemo = memo(EditorContent);

const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            // extend the existing attributes …
            ...this.parent?.(),

            // and add a new one …
            backgroundColor: {
                default: null,
                parseHTML: (element) =>
                    element.getAttribute('data-background-color'),
                renderHTML: (attributes) => {
                    return {
                        'data-background-color': attributes.backgroundColor,
                        style: `background-color: ${attributes.backgroundColor}`,
                    };
                },
            },
            fontColor: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-font-color'),
                renderHTML: (attributes) => {
                    return {
                        'data-font-color': attributes.fontColor,
                        style: `color: ${attributes.fontColor}`,
                    };
                },
            },
            align: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-font-align'),
                renderHTML: (attributes) => {
                    return {
                        'data-font-align': attributes.align,
                        style: `text-align: ${attributes.align}`,
                    };
                },
            },
        };
    },
});

export const RichTextEditor = ({
    onChange = () => undefined,
    value = tableHTML,
}: RichTextEditorProps): JSX.Element => {
    const [focused, setFocused] = useState(false);

    const editor = useEditor(
        {
            extensions: [
                StarterKit,
                Table,
                TableRow,
                TableHeader,
                CustomTableCell,
                Underline,
                Superscript,
                Subscript,
            ],

            content: '',
            onUpdate: (e) => onChange(e.editor.getHTML()),
            editorProps: {
                attributes: {
                    class: 'rte-input',
                },
            },
            onBlur: () => setFocused(false),
            onFocus: () => setFocused(true),
        },
        []
    );

    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        const { selection } = editor.state;
        editor.commands.setContent(value, false, {
            preserveWhitespace: 'full',
        });
        editor.commands.setTextSelection(selection);
        editor.view.focus();
    }, [editor, value]);
    const [open, setOpen] = useState(false);

    return (
        <Stack
            sx={{
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                borderRadius: '8px',
                boxShadow: focused ? '0 0 0 2px #0C6D36' : undefined,
                '.rte-input': {
                    padding: '8.5px 14px',
                    borderRadius: '16px',
                    outline: 'none',
                },
            }}
        >
            <Button onClick={() => setOpen(true)}>Настройки таблицы</Button>
            <TableEditorDialog
                setOpen={(show: boolean) => setOpen(show)}
                open={open}
            >
                <MenuBar editor={editor} />
                <EditorMemo editor={editor} />
            </TableEditorDialog>
        </Stack>
    );
};
